import axios from 'axios';
import { shutdownIntercom } from '../utils/intercom/intercomUtils';
import { authEndpoints, requestedPathKey } from './API.constants';
import API from './API';

function getBaseURL(): string {
  if (typeof Cypress === 'undefined') {
    if (import.meta.env.DEV) {
      return `${window.location.protocol}//${window.location.host}/`;
    }

    // If on sandbox, we don't have a specific .env file thus we need to set it here
    if (window.location.host === 'sandbox.hostfully.com') {
      return 'https://sandbox-integrations.hostfully.com/';
    }

    return import.meta.env.VITE_INTEGRATIONS_API_URL;
  }

  return Cypress.env('INTEGRATIONS_API_URL');
}

const IntegrationsAPI = axios.create({
  baseURL: getBaseURL(),
});

const loginHash = '#/login';
const logoutHash = '#/logout';

function logout() {
  const currentHash = window.location.hash;

  if (currentHash.startsWith(loginHash)) {
    return;
  }

  shutdownIntercom();

  if (currentHash !== logoutHash) {
    sessionStorage.setItem(requestedPathKey, currentHash);
  }

  window.location.hash = logoutHash;
}

IntegrationsAPI.interceptors.request.use(
  (config) => {
    const accessToken = localStorage.getItem('access_token');
    if (accessToken && config.headers) {
      config.headers.Authorization = accessToken;
    }

    return config;
  },
  (error) => {
    Promise.reject(error);
  },
);

IntegrationsAPI.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (
      (error.response.status === 404 || error.response.status === 403) &&
      originalRequest.url === authEndpoints.refreshToken
    ) {
      logout();
      return Promise.reject(error);
    }

    if (error.response.status === 401) {
      if (originalRequest._retry) {
        logout();
      } else {
        originalRequest._retry = true;
        const refreshToken = localStorage.getItem('refresh_token');

        if (refreshToken) {
          const response = await API.post(authEndpoints.refreshToken, {
            grantType: 'REFRESH_TOKEN',
            refreshToken,
          });
          if (response.status === 202) {
            localStorage.setItem('access_token', response.data.accessToken);
            localStorage.setItem('refresh_token', response.data.refreshToken);
            return IntegrationsAPI(originalRequest);
          }
        }

        logout();
      }
    }

    return Promise.reject(error);
  },
);

export default IntegrationsAPI;
